import { ReactElement, useState } from 'react';
import { Box, Link, TextField } from '@hopskipdrive/react-components';
import { Add, Remove } from '@mui/icons-material';

import ChipSelect, { ChipSelectOption } from '../ChipSelect';

import { ClearButton, Error, SingleTypeLinkContainer, TypeLinkBorder } from './MultipleTypeLinks.styles';

export interface TypeLinkValue {
  type?: string;
  value?: string;
}

export interface MultipleTypeLinksProps {
  label?: ReactElement;
  types: ChipSelectOption[];
  value: TypeLinkValue[] | [];
  onChange: (value: TypeLinkValue[] | null) => void;
  typePlaceholder?: string;
  linkPlaceholder?: string;
  error?: Array<object> | { type?: string | object; value?: string | object; message?: string };
}

interface SingleTypeLinkProps {
  types: ChipSelectOption[];
  value: TypeLinkValue | null;
  onChange: (newValue: TypeLinkValue) => void;
  typePlaceholder?: string;
  linkPlaceholder?: string;
  label?: ReactElement;
  endButton?: ReactElement;
  rootError?: boolean;
  error?: { type: { message: string }; value: { message: string } };
}

const SingleTypeLink = ({
  types,
  value,
  onChange,
  typePlaceholder,
  linkPlaceholder,
  label,
  endButton,
  error,
  rootError,
}: SingleTypeLinkProps): ReactElement => {
  const [typeValue, setTypeValue] = useState<string>(value?.type || '');
  const [inputValue, setInputValue] = useState<string>(value?.value || '');

  const updateValue = (val: string | null) => {
    onChange({ type: val || typeValue, value: inputValue });
  };

  return (
    <SingleTypeLinkContainer>
      {label}
      <ChipSelect
        options={types}
        value={typeValue}
        onChange={(val) => {
          setTypeValue(val || '');
          updateValue(val);
        }}
        placeholder={typePlaceholder}
        error={!!error?.type?.message || rootError}
        helperText={error?.type?.message}
      />
      <TypeLinkBorder />

      <TextField
        variant="outlined"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={linkPlaceholder}
        onBlur={() => updateValue(null)}
        error={!!error?.value?.message || rootError}
        helperText={error?.value?.message}
      />
      {endButton}
    </SingleTypeLinkContainer>
  );
};

const MultipleTypeLinks = ({
  label,
  types,
  value,
  onChange,
  typePlaceholder,
  linkPlaceholder,
  error,
}: MultipleTypeLinksProps): ReactElement => {
  const handleSingleChange = (index: number, newSingleValue: TypeLinkValue) => {
    if (!value) {
      onChange([newSingleValue]);
    } else if (value.length <= index) {
      onChange([...value, newSingleValue]);
    } else {
      const newValue = [...value];

      newValue[index] = newSingleValue;
      onChange(newValue);
    }
  };

  const addRow = () => {
    if (value && value.length) {
      onChange([...value, { type: '', value: '' }]);
    } else {
      onChange([
        { type: '', value: '' },
        { type: '', value: '' },
      ]);
    }
  };

  const removeRow = (index: number) => {
    onChange(value ? value.filter((_, ind) => ind !== index) : []);
  };

  return (
    <Box>
      {value &&
        value.map((item, index) => (
          <SingleTypeLink
            key={index}
            label={label}
            value={item}
            onChange={(newSingleValue) => {
              handleSingleChange(index, newSingleValue);
            }}
            types={types}
            typePlaceholder={typePlaceholder}
            linkPlaceholder={linkPlaceholder}
            error={error && error[index] ? error[index] : undefined}
            endButton={
              <Link onClick={() => {}}>
                {index === 0 ? <Add onClick={addRow} /> : <Remove onClick={() => removeRow(index)} />}
              </Link>
            }
          />
        ))}
      {(!value || !value.length) && (
        <SingleTypeLink
          label={label}
          value={{}}
          onChange={(newSingleValue) => {
            if (newSingleValue.value === '' && newSingleValue.type === '') {
              return;
            }
            handleSingleChange(0, newSingleValue);
          }}
          types={types}
          typePlaceholder={typePlaceholder}
          linkPlaceholder={linkPlaceholder}
          rootError={error && !Array.isArray(error) && error?.type === 'root'}
          error={error ? error[0] : undefined}
          endButton={
            <Link onClick={() => {}}>
              <Add onClick={addRow} />
            </Link>
          }
        />
      )}
      {value && value.length > 0 && (
        <Box textAlign="right">
          <ClearButton onClick={() => onChange([])}>Clear All</ClearButton>
        </Box>
      )}

      {error && !Array.isArray(error) && error?.type === 'root' && <Error variant="body2">{error?.message}</Error>}
    </Box>
  );
};

export default MultipleTypeLinks;
